import { useState, useEffect, useCallback } from 'react'
import {
  Timer, RefreshCw, Shield, Calendar, AlertTriangle, Cloud, Activity,
  CheckCircle2, RotateCcw, ExternalLink, Zap,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── Types ────────────────────────────────────────────────────────────────────

interface Clock {
  id: string
  kind: 'until' | 'since'
  category: 'security' | 'evergreen' | 'availability' | 'platform'
  label: string
  target_date: string
  days: number
  minutes?: number
  display?: string
  severity: 'good' | 'warn' | 'bad'
  source: string
  detail: string
  link: string | null
  resettable?: boolean
}

interface Board {
  today: string
  count: number
  by_severity: Record<string, number>
  clocks: Clock[]
  generated_at: string
}

// ── Visuals ──────────────────────────────────────────────────────────────────

const SEVERITY_BG: Record<string, string> = {
  good: 'bg-emerald/10 border-emerald/30',
  warn: 'bg-amber/10 border-amber/30',
  bad:  'bg-rose/10 border-rose/30',
}
const SEVERITY_TEXT: Record<string, string> = {
  good: 'text-emerald',
  warn: 'text-amber-light',
  bad:  'text-rose',
}
const SEVERITY_NUMBER: Record<string, string> = {
  good: 'text-emerald',
  warn: 'text-amber',
  bad:  'text-rose',
}

const CATEGORY_ICON: Record<string, React.ElementType> = {
  security:    Shield,
  evergreen:   Calendar,
  availability:Cloud,
  platform:    Activity,
}

// ── Big numeric tile ─────────────────────────────────────────────────────────

function ClockTile({ clock, onReset }: { clock: Clock; onReset: () => void }) {
  const Icon = CATEGORY_ICON[clock.category] || Timer
  const today = new Date().getTime()
  const target = new Date(clock.target_date).getTime()
  // For "until" we want days remaining; for "since" we want days elapsed.
  const numericDays = clock.days
  const display = clock.display ?? (
    Math.abs(numericDays) === 0 ? 'Today' :
    Math.abs(numericDays) === 1 ? '1 day' :
    `${Math.abs(numericDays)} days`
  )
  const kindLabel = clock.kind === 'until' ? 'until' : 'since'
  const subline = new Date(clock.target_date).toUTCString().slice(5, 16)

  return (
    <div className={clsx('card p-4 border-2 flex flex-col gap-2', SEVERITY_BG[clock.severity])}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon size={12} className={SEVERITY_TEXT[clock.severity]} />
          <span className={clsx('text-[10px] uppercase tracking-wider font-semibold', SEVERITY_TEXT[clock.severity])}>
            {kindLabel}
          </span>
          <span className="text-[9px] text-text-muted uppercase">· {clock.category}</span>
        </div>
        {clock.resettable && (
          <button
            type="button"
            onClick={() => { if (confirm(`Reset "${clock.label}" counter to today?`)) onReset() }}
            className="text-text-muted hover:text-text-primary"
            title="Reset counter"
          >
            <RotateCcw size={11} />
          </button>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span className={clsx('text-4xl font-bold font-mono leading-none', SEVERITY_NUMBER[clock.severity])}>
          {clock.display ?? Math.abs(numericDays)}
        </span>
        {!clock.display && (
          <span className={clsx('text-sm font-medium', SEVERITY_TEXT[clock.severity])}>
            {Math.abs(numericDays) === 1 ? 'day' : 'days'}
          </span>
        )}
      </div>

      <div>
        <div className="text-[12px] font-semibold text-text-primary leading-snug">{clock.label}</div>
        <div className="text-[10px] text-text-muted">{subline}</div>
      </div>

      <p className="text-[10px] text-text-secondary leading-relaxed flex-1">{clock.detail}</p>

      {clock.link && (
        <a
          href={clock.link}
          target="_blank"
          rel="noreferrer noopener"
          className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1"
        >
          More <ExternalLink size={9} />
        </a>
      )}
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function CountdownBoardPage() {
  const [board, setBoard] = useState<Board | null>(null)
  const [loading, setLoading] = useState(true)
  // Tick every minute so "since" counters drift forward without a refresh
  const [, setTick] = useState(0)

  const fetchBoard = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/countdowns')
      setBoard(data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchBoard() }, [fetchBoard])

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  const resetClock = async (id: string) => {
    await api.post(`/api/countdowns/reset/${id}`)
    fetchBoard()
  }

  if (loading && !board) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }
  if (!board) return <div className="text-text-muted text-sm">No data</div>

  const untilClocks = board.clocks.filter(c => c.kind === 'until')
  const sinceClocks = board.clocks.filter(c => c.kind === 'since')

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Timer size={20} className="text-amber" /> Countdown Board
          </h1>
          <p className="text-[10px] text-text-secondary">
            {board.count} clocks — pressure metrics for what's coming and what just happened.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[10px]">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-rose" />
              <span className="text-text-secondary">{board.by_severity.bad || 0} bad</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber" />
              <span className="text-text-secondary">{board.by_severity.warn || 0} warn</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald" />
              <span className="text-text-secondary">{board.by_severity.good || 0} good</span>
            </span>
          </div>
          <button type="button" onClick={fetchBoard} className="btn-ghost text-xs" title="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Until — coming up */}
      {untilClocks.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber" />
            Coming up
            <span className="text-[10px] text-text-muted font-normal">deadlines & scheduled events</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {untilClocks.map(c => (
              <ClockTile key={c.id} clock={c} onReset={() => resetClock(c.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Since — last bad event */}
      {sinceClocks.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <CheckCircle2 size={14} className="text-emerald" />
            Days since
            <span className="text-[10px] text-text-muted font-normal">streaks since the last bad thing</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sinceClocks.map(c => (
              <ClockTile key={c.id} clock={c} onReset={() => resetClock(c.id)} />
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-text-muted">
        Patch Tuesday computed locally · CVE / outage / ingestion clocks pulled from live data ·
        Generated {board.generated_at.slice(11, 19)} UTC. Page auto-refreshes calculations every minute.
      </div>
    </div>
  )
}
